/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Viewer from 'react-viewer';
import fileDownload from 'js-file-download';
import FileService from 'services/FileService';
import DialogDefaultV2 from '../DialogDefaultV2';
import PdfPreviewPanel from '../filePreview/PdfPreviewPanel';
import ExcelPreviewPanel from '../filePreview/ExcelPreviewPanel';
import {
  getAttachmentDisplayName,
  getDownloadFileName,
  isExcelAttachment,
  isImageAttachment,
  isPdfAttachment,
  normalizeAttachment,
} from '../filePreview/attachmentTypes';

const loadAttachmentBlob = async (attachment, lazyLoad) => {
  if (attachment.file instanceof Blob) {
    return attachment.file;
  }
  if (attachment.src && attachment.src.startsWith('data:')) {
    const response = await fetch(attachment.src);
    return response.blob();
  }
  if (lazyLoad && attachment.downloadUrl) {
    const response = await FileService.getFile(attachment.downloadUrl);
    return response.data;
  }
  return null;
};

const AnexoPreviewV2 = ({
  open,
  onClose,
  attachments,
  initialIndex,
  title,
  lazyLoad,
}) => {
  const normalizedAttachments = useMemo(
    () => (attachments || []).map(normalizeAttachment),
    [attachments],
  );

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [loadedFiles, setLoadedFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const currentAttachment = normalizedAttachments[activeIndex] || null;

  useEffect(() => {
    if (open) {
      setActiveIndex(initialIndex);
    } else {
      setLoadedFiles({});
      setLoadError(null);
      setImageViewerOpen(false);
    }
  }, [open, initialIndex]);

  const currentCacheKey = currentAttachment?.downloadUrl || String(activeIndex);
  const currentLoaded = loadedFiles[currentCacheKey];
  const currentBlob = currentLoaded?.blob || null;
  const isCached = Boolean(currentLoaded);

  useEffect(() => {
    if (!open || !currentAttachment || isCached) {
      if (isCached) setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchCurrent = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const blob = await loadAttachmentBlob(currentAttachment, lazyLoad);
        if (cancelled) return;

        if (!blob) {
          setLoadError('Não foi possível carregar o arquivo.');
          return;
        }

        setLoadedFiles((prev) => ({
          ...prev,
          [currentCacheKey]: {
            blob,
            mimeType: blob.type || currentAttachment.mimeType,
          },
        }));
      } catch (e) {
        if (!cancelled) {
          setLoadError('Erro ao carregar o anexo.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCurrent();

    return () => {
      cancelled = true;
    };
  }, [open, currentAttachment, currentCacheKey, lazyLoad, isCached]);

  const imageAttachments = useMemo(
    () => normalizedAttachments.filter(isImageAttachment),
    [normalizedAttachments],
  );

  const imageViewerImages = useMemo(() => {
    return imageAttachments.map((item) => {
      const key = item.downloadUrl || item.nome;
      const loaded = loadedFiles[key];
      if (loaded?.blob) {
        return {
          src: URL.createObjectURL(loaded.blob),
          alt: getAttachmentDisplayName(item),
        };
      }
      if (item.src) {
        return { src: item.src, alt: getAttachmentDisplayName(item) };
      }
      return { src: '', alt: getAttachmentDisplayName(item) };
    }).filter((img) => img.src);
  }, [imageAttachments, loadedFiles]);

  const imageViewerIndex = useMemo(() => {
    if (!currentAttachment || !isImageAttachment(currentAttachment)) return 0;
    return imageAttachments.findIndex(
      (item) => item.downloadUrl === currentAttachment.downloadUrl
        && item.nome === currentAttachment.nome,
    );
  }, [currentAttachment, imageAttachments]);

  const handleDownload = useCallback(async () => {
    if (!currentAttachment) return;
    try {
      const blob = currentBlob || await loadAttachmentBlob(currentAttachment, lazyLoad);
      if (blob) {
        fileDownload(blob, getDownloadFileName(currentAttachment));
      }
    } catch (e) {
      setLoadError('Erro ao baixar o arquivo.');
    }
  }, [currentAttachment, currentBlob, lazyLoad]);

  const goPrev = () => {
    setActiveIndex((idx) => Math.max(0, idx - 1));
    setImageViewerOpen(false);
  };

  const goNext = () => {
    setActiveIndex((idx) => Math.min(normalizedAttachments.length - 1, idx + 1));
    setImageViewerOpen(false);
  };

  const handleClose = () => {
    setImageViewerOpen(false);
    onClose();
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <Typography variant="body2" color="error">
            {loadError}
          </Typography>
          <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={handleDownload}>
            Baixar arquivo
          </Button>
        </Stack>
      );
    }

    if (!currentAttachment) {
      return (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
          Nenhum anexo selecionado.
        </Typography>
      );
    }

    if (isImageAttachment(currentAttachment)) {
      const src = currentBlob
        ? URL.createObjectURL(currentBlob)
        : currentAttachment.src;

      if (!src) {
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
            Carregando imagem...
          </Typography>
        );
      }

      return (
        <Stack spacing={2} alignItems="center">
          <Box
            component="img"
            alt={getAttachmentDisplayName(currentAttachment)}
            src={src}
            sx={{
              maxWidth: '100%',
              maxHeight: '55vh',
              objectFit: 'contain',
              borderRadius: 1,
              cursor: 'zoom-in',
            }}
            onClick={() => setImageViewerOpen(true)}
          />
          <Button variant="outlined" size="small" onClick={() => setImageViewerOpen(true)}>
            Ampliar
          </Button>
        </Stack>
      );
    }

    if (isPdfAttachment(currentAttachment)) {
      return <PdfPreviewPanel file={currentBlob} />;
    }

    if (isExcelAttachment(currentAttachment)) {
      return <ExcelPreviewPanel file={currentBlob} />;
    }

    return (
      <Stack spacing={2} alignItems="center" sx={{ py: 4, px: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
          Pré-visualização não disponível
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Não conseguimos exibir este tipo de arquivo no navegador. Você pode baixá-lo para abrir localmente.
        </Typography>
        <Button variant="contained" startIcon={<FileDownloadOutlinedIcon />} onClick={handleDownload}>
          Baixar arquivo
        </Button>
      </Stack>
    );
  };

  const dialogTitle = normalizedAttachments.length > 1
    ? `${title} (${activeIndex + 1}/${normalizedAttachments.length})`
    : title;

  const headerActions = [];

  if (normalizedAttachments.length > 1) {
    headerActions.push(
      {
        title: 'Anterior',
        onClick: goPrev,
        show: activeIndex > 0,
        disabled: activeIndex <= 0,
      },
      {
        title: 'Próximo',
        onClick: goNext,
        show: activeIndex < normalizedAttachments.length - 1,
        disabled: activeIndex >= normalizedAttachments.length - 1,
      },
    );
  }

  if (currentAttachment && (currentBlob || currentAttachment.downloadUrl)) {
    headerActions.push({
      title: 'Baixar',
      onClick: handleDownload,
      show: true,
    });
  }

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={handleClose}
        readOnly
        title={dialogTitle}
        maxWidth="md"
        fullWidth
        headerAction={headerActions.length ? headerActions : undefined}
      >
        {normalizedAttachments.length > 1 ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <IconNavButton disabled={activeIndex <= 0} onClick={goPrev} icon={<NavigateBeforeIcon />} />
            <Typography variant="body2" sx={{ flex: 1, textAlign: 'center' }} noWrap>
              {getAttachmentDisplayName(currentAttachment)}
            </Typography>
            <IconNavButton
              disabled={activeIndex >= normalizedAttachments.length - 1}
              onClick={goNext}
              icon={<NavigateNextIcon />}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getAttachmentDisplayName(currentAttachment)}
          </Typography>
        )}
        {renderPreviewContent()}
      </DialogDefaultV2>

      <Viewer
        visible={open && imageViewerOpen && imageViewerImages.length > 0}
        onClose={() => setImageViewerOpen(false)}
        images={imageViewerImages}
        activeIndex={imageViewerIndex >= 0 ? imageViewerIndex : 0}
        zIndex={4000}
        scalable
        zoomSpeed={0.25}
        downloadable
      />
    </>
  );
};

const IconNavButton = ({ disabled, onClick, icon }) => (
  <Button
    variant="outlined"
    size="small"
    disabled={disabled}
    onClick={onClick}
    sx={{ minWidth: 36, px: 1 }}
  >
    {icon}
  </Button>
);

IconNavButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
};

AnexoPreviewV2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attachments: PropTypes.arrayOf(PropTypes.object),
  initialIndex: PropTypes.number,
  title: PropTypes.string,
  lazyLoad: PropTypes.bool,
};

AnexoPreviewV2.defaultProps = {
  attachments: [],
  initialIndex: 0,
  title: 'Anexos',
  lazyLoad: true,
};

export default AnexoPreviewV2;
